import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { Field, FileField, type ISchema, message, useForm } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  DataGridPro,
  type GridColDef,
  type GridRowOrderChangeParams,
} from '@mui/x-data-grid-pro'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  SiteDocument,
  SportCategory,
  UpdateSiteCourtsDocument,
  UpdateSiteDocument,
} from '../gql/graphql'

const SPORT_CATEGORY_LABELS: Record<SportCategory, string> = {
  [SportCategory.Tennis]: '网球',
  [SportCategory.Pickleball]: '匹克球',
}

gql`
  query Site($siteId: UUID!) {
    site(siteId: $siteId) {
      id
      name
      location
      phone
      images
      avatar
      courts {
        id
        name
        displayOrder
        sport
      }
    }
  }
`

gql`
  mutation UpdateSite($siteId: ID!, $siteInput: SiteInput!) {
    updateSite(siteId: $siteId, siteInput: $siteInput) {
      id
      name
      location
      phone
      images
      avatar
    }
  }
`

// Created: 2026-02-15 | Initial commit: Simplify courts management with single mutation
// Update all courts for a site in one operation
gql`
  mutation UpdateSiteCourts($siteId: ID!, $courts: [CourtInput!]!) {
    updateSiteCourts(siteId: $siteId, courts: $courts) {
      id
      name
      displayOrder
      sport
    }
  }
`

const schema: ISchema = {
  rules: {
    name: 'required',
    location: 'required',
    phone: 'required',
  },
  messages: {
    'required.name': '请填写场馆名称',
    'required.location': '请填写场馆地址',
    'required.phone': '请填写联系电话',
  },
}

type SiteFormValues = {
  name: string
  location: string
  phone: string
  images: string[]
  avatar: string[]
}

type CourtRow = {
  id: string
  name: string
  displayOrder: number
  sport: SportCategory
  isNew?: boolean
  __reorder__: string
}

export default function EditSitePage() {
  const { siteId } = useParams<{ siteId: string }>()
  const navigate = useNavigate()

  const form = useForm<SiteFormValues>(
    {
      name: '',
      location: '',
      phone: '',
      images: [],
      avatar: [],
    },
    schema,
  )

  const { data, loading, error } = useQuery(SiteDocument, {
    // biome-ignore lint/style/noNonNullAssertion: siteId is checked before this component renders
    variables: { siteId: siteId! },
    skip: !siteId,
    fetchPolicy: 'network-only',
  })

  // Load site data into form when query completes
  useEffect(() => {
    if (data?.site) {
      form.update({
        name: data.site.name,
        location: data.site.location,
        phone: data.site.phone,
        images: data.site.images ?? [],
        avatar: data.site.avatar ? [data.site.avatar] : [],
      })
    }
  }, [data, form])

  const [updateSite, { loading: updating }] = useMutation(UpdateSiteDocument, {
    onError: (error) => {
      alert(`更新场馆失败: ${error.message}`)
    },
  })

  // Single court mutation
  const [updateSiteCourts] = useMutation(UpdateSiteCourtsDocument)

  // Local courts state and dirty tracking
  const [localCourts, setLocalCourts] = useState<CourtRow[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Court dialog state for adding new courts
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newCourtName, setNewCourtName] = useState('')
  const [newCourtCategory, setNewCourtCategory] = useState<SportCategory>(
    SportCategory.Tennis,
  )

  // Initialize local courts from query data
  useEffect(() => {
    if (data?.site?.courts) {
      setLocalCourts(
        data.site.courts.map((c) => ({
          id: c.id,
          name: c.name,
          displayOrder: c.displayOrder,
          sport: c.sport,
          __reorder__: c.name,
        })),
      )
      setIsDirty(false)
    }
  }, [data])

  const handleSubmit = async () => {
    if (!siteId) return

    const validationErrors = form.validate()

    // void means no error, so only return if there are actual errors
    if (validationErrors) {
      return
    }

    const { name, location, phone, images, avatar } = form.values

    try {
      await updateSite({
        variables: {
          siteId: siteId,
          siteInput: {
            name: name.trim(),
            location: location.trim(),
            phone: phone.trim(),
            images: images.length > 0 ? images : null,
            avatar: avatar.length > 0 ? avatar[0] : null,
          },
        },
      })
      message.success('场馆信息已保存')
    } catch (error) {
      console.error('Failed to update site:', error)
    }
  }

  const handleBack = () => {
    navigate('/sites')
  }

  const handleAddCourtClick = () => {
    setNewCourtName('')
    setNewCourtCategory(SportCategory.Tennis)
    setAddDialogOpen(true)
  }

  const handleAddCourtSubmit = () => {
    const trimmed = newCourtName.trim()
    if (!trimmed) return

    // Add to local state with temporary ID
    const tempId = `temp-${Date.now()}`
    const maxOrder =
      localCourts.length > 0 ? Math.max(...localCourts.map((c) => c.displayOrder)) : -1
    setLocalCourts([
      ...localCourts,
      {
        id: tempId,
        name: trimmed,
        displayOrder: maxOrder + 1,
        sport: newCourtCategory,
        isNew: true,
        __reorder__: trimmed,
      },
    ])
    setIsDirty(true)
    setAddDialogOpen(false)
  }

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const { oldIndex, targetIndex } = params
    const reordered = [...localCourts]
    const [movedRow] = reordered.splice(oldIndex, 1)
    reordered.splice(targetIndex, 0, movedRow)

    // Update display orders
    const updated = reordered.map((court, index) => ({
      ...court,
      displayOrder: index,
    }))

    setLocalCourts(updated)
    setIsDirty(true)
  }

  const handleSaveCourts = async () => {
    if (!siteId) return

    setIsSaving(true)
    try {
      // Convert local courts to input format
      const courtsInput = localCourts.map((court) => ({
        id: court.isNew ? null : court.id,
        name: court.name,
        displayOrder: court.displayOrder,
        sport: court.sport,
      }))

      const result = await updateSiteCourts({
        variables: { siteId, courts: courtsInput },
      })

      // Update local state with real IDs from mutation result
      if (result.data?.updateSiteCourts) {
        setLocalCourts(
          result.data.updateSiteCourts.map((c) => ({
            id: c.id,
            name: c.name,
            displayOrder: c.displayOrder,
            sport: c.sport,
            __reorder__: c.name,
          })),
        )
      }

      setIsDirty(false)
      message.success('场地信息已保存')
    } catch (error) {
      alert(`保存场地失败: ${error instanceof Error ? error.message : '未知错误'}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetCourts = () => {
    if (data?.site?.courts) {
      setLocalCourts(
        data.site.courts.map((c) => ({
          id: c.id,
          name: c.name,
          displayOrder: c.displayOrder,
          sport: c.sport,
          __reorder__: c.name,
        })),
      )
      setIsDirty(false)
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: '场地名称',
      flex: 1,
      editable: true,
    },
    {
      field: 'sport',
      headerName: '类型',
      width: 110,
      type: 'singleSelect',
      valueOptions: Object.values(SportCategory).map((v) => ({
        value: v,
        label: SPORT_CATEGORY_LABELS[v],
      })),
      editable: true,
      valueFormatter: (value: SportCategory) => SPORT_CATEGORY_LABELS[value] ?? value,
    },
  ]

  if (!siteId) {
    console.error('EditSitePage: siteId is required but was not provided')
    return null
  }

  if (error) {
    console.error('EditSitePage: Failed to load site data', error)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">编辑场馆</Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Field
          form={form}
          name="name"
          label="场馆名称"
          disabled={updating || loading}
        />
        <Field
          form={form}
          name="location"
          label="场馆地址"
          disabled={updating || loading}
        />
        <Field
          form={form}
          name="phone"
          label="联系电话"
          type="tel"
          disabled={updating || loading}
          inputProps={{ pattern: '[0-9\\-]*' }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key.length === 1 && !/[\d-]/.test(e.key)) e.preventDefault()
          }}
        />
        <Box sx={{ mt: 2 }}>
          <Field component={FileField} form={form} name="avatar" label="场馆头像" />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Field component={FileField} form={form} name="images" label="场馆图片" />
        </Box>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={updating || loading}
            onClick={handleSubmit}
          >
            {updating ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 600, mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">场地管理</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isDirty && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  size="small"
                  onClick={handleResetCourts}
                  disabled={isSaving}
                >
                  重置
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  size="small"
                  onClick={handleSaveCourts}
                  disabled={isSaving}
                >
                  {isSaving ? '保存中...' : '保存'}
                </Button>
              </>
            )}
            <Button startIcon={<AddIcon />} size="small" onClick={handleAddCourtClick}>
              添加场地
            </Button>
          </Box>
        </Box>

        {localCourts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            暂无场地，请点击"添加场地"创建。
          </Typography>
        ) : (
          <Box
            sx={{ height: Math.min(400, localCourts.length * 52 + 56), width: '100%' }}
          >
            <DataGridPro
              rows={localCourts}
              columns={columns}
              rowReordering
              onRowOrderChange={handleRowOrderChange}
              processRowUpdate={(newRow) => {
                setLocalCourts((prev) =>
                  prev.map((court) =>
                    court.id === newRow.id
                      ? { ...newRow, __reorder__: newRow.name }
                      : court,
                  ),
                )
                setIsDirty(true)
                return newRow
              }}
              getRowId={(row) => row.id}
              hideFooter
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'move',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        sx={{ '& .MuiDialog-paper': { width: 350 } }}
      >
        <DialogTitle>添加场地</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="场地名称"
            value={newCourtName}
            onChange={(e) => setNewCourtName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddCourtSubmit()
              }
            }}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>类型</InputLabel>
            <Select
              label="类型"
              value={newCourtCategory}
              onChange={(e) => setNewCourtCategory(e.target.value as SportCategory)}
            >
              {Object.values(SportCategory).map((v) => (
                <MenuItem key={v} value={v}>
                  {SPORT_CATEGORY_LABELS[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleAddCourtSubmit}
            variant="contained"
            disabled={!newCourtName.trim()}
          >
            确定
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
